import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useContent } from '../../context/ContentContext';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const HERO_POSTER = "/images/hero-poster.png";
const HERO_VIDEO = "/images/hero-video.mp4";

export default function HeroSection({ onEnquire, onDealerClick }) {
  const { t, lang } = useLanguage();
  const { content } = useContent();
  const h = content?.hero?.[lang] || t.hero;
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{
        backgroundImage: `url(${HERO_POSTER})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <video
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        poster={HERO_POSTER}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ zIndex: 0 }}
      >
        <source src={HERO_VIDEO} type="video/mp4" />
      </video>
      <div className="absolute inset-0" style={{ background: 'linear-gradient(160deg, rgba(10,63,34,0.82) 0%, rgba(10,63,34,0.65) 50%, rgba(10,63,34,0.80) 100%)', zIndex: 1 }} />
      <div className="relative mx-auto max-w-4xl px-4 sm:px-6 text-center" style={{ zIndex: 2 }}>
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          <motion.span
            variants={fadeUp}
            className="inline-block mb-6 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-[0.2em]"
            style={{ backgroundColor: 'rgba(217,119,6,0.25)', color: '#FCD34D', border: '1px solid rgba(217,119,6,0.4)' }}
          >
            {h.badge}
          </motion.span>

          <motion.h1 variants={fadeUp} className="font-heading font-black text-white mb-3 leading-tight" style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}>
            <span className="block">SHREE RAM</span>
            <span className="block" style={{ color: '#FCD34D' }}>AGRO INDUSTRIES</span>
          </motion.h1>

          <motion.p variants={fadeUp} className="text-xl sm:text-2xl font-heading font-semibold text-white/90 mb-2">
            {h.tagline} <span style={{ color: '#FCD34D' }}>{h.tagline2}</span>
          </motion.p>

          <motion.p variants={fadeUp} className="text-sm sm:text-base text-white/70 mb-8 max-w-xl mx-auto leading-relaxed">
            {h.subtitle}
          </motion.p>

          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              data-testid="hero-cta-farmer"
              onClick={onEnquire}
              className="px-8 py-4 rounded-lg text-base font-heading font-bold text-white transition-all duration-300 hover:scale-105 hover:shadow-2xl"
              style={{ backgroundColor: '#0A3F22', boxShadow: '0 4px 20px rgba(10,63,34,0.4)' }}
            >
              {h.ctaFarmer}
            </button>
            <button
              data-testid="hero-cta-dealer"
              onClick={onDealerClick}
              className="px-8 py-4 rounded-lg text-base font-heading font-bold text-white transition-all duration-300 hover:scale-105 hover:shadow-2xl"
              style={{ backgroundColor: '#D97706', boxShadow: '0 4px 20px rgba(217,119,6,0.4)' }}
            >
              {h.ctaDealer}
            </button>
          </motion.div>
        </motion.div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50">
        <span className="text-xs font-body">{t.hero.scrollHint}</span>
        <ChevronDown size={20} className="animate-bounce" />
      </div>
    </section>
  );
}
